import React, { useState, useEffect } from 'react';
import {
  History,
  ArrowLeft,
  Calendar,
  Layers,
  Search,
  Key
} from 'lucide-react';
import { getSearchHistory, getHistoryById } from '../utils/api';
import ResultsTable from '../components/ResultsTable';
import { useToast } from '../hooks/useToast';

export default function HistoryPage() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await getSearchHistory();
      setHistory(res.data.data || []);
    } catch {
      toast('Failed to load search history', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectHistory = async (item) => {
    setSelectedLoading(true);
    setSelected(null);
    try {
      const res = await getHistoryById(item.id || item._id);
      setSelected(res.data.data);
    } catch {
      toast('Failed to load search details', 'error');
    } finally {
      setSelectedLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (selected) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setSelected(null)}
          >
            <ArrowLeft size={14} />
            Back to History
          </button>
          <span style={{ color: 'var(--text-muted)' }}>/</span>
          <strong style={{ color: 'var(--text-primary)', fontSize: '0.95rem' }}>
            "{selected.query}"
          </strong>
        </div>

        <ResultsTable
          results={selected.results}
          query={selected.query}
          category={selected.category}
          searchedAt={selected.searchedAt}
        />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <History size={22} color="var(--color-indigo)" />
          Extraction History
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          Review past lead extractions, inspect results, or re-download Excel spreadsheets.
        </p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
          <span className="spinner" style={{ margin: '0 auto 0.75rem' }} />
          Loading extraction history...
        </div>
      ) : history.length === 0 ? (
        <div className="admin-card" style={{ textAlign: 'center', padding: '3rem' }}>
          <Layers size={36} color="var(--text-muted)" style={{ margin: '0 auto 1rem' }} />
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
            No Search History Yet
          </h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.825rem' }}>
            When you extract business leads from Google Maps, they will appear here.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1rem' }}>
          {history.map((item) => (
            <div
              key={item.id || item._id}
              className="admin-card"
              style={{ cursor: 'pointer', transition: 'border-color 0.15s ease' }}
              onClick={() => handleSelectHistory(item)}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <strong style={{ fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                  {item.query}
                </strong>
                <span className="badge badge-indigo">
                  {item.totalResults || item.results?.length || 0} Leads
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                {item.location && (
                  <div>Location: {item.location}</div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Calendar size={13} />
                  <span>{formatDate(item.searchedAt)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
